// Tiny localStorage-backed cart for the multi-stop trip planner.
// Stores an ordered list of depot IDs and their display metadata so the
// planner page can render without re-fetching before optimization.

import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "nadt.tripCart.v1";
const EVENT = "nadt.tripCart.changed";

function read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event(EVENT));
}

export function useTripCart() {
  const [items, setItems] = useState(() => read());

  useEffect(() => {
    const sync = () => setItems(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const has = useCallback((id) => items.some((i) => i.id === id), [items]);

  const add = useCallback((depot) => {
    const current = read();
    if (current.some((i) => i.id === depot.id)) return current;
    if (current.length >= 25) return current;
    const next = [
      ...current,
      {
        id: depot.id,
        name: depot.name,
        city: depot.city,
        state_code: depot.state_code,
        latitude: depot.latitude,
        longitude: depot.longitude,
      },
    ];
    write(next);
    return next;
  }, []);

  const remove = useCallback((id) => {
    const next = read().filter((i) => i.id !== id);
    write(next);
    return next;
  }, []);

  const clear = useCallback(() => {
    write([]);
  }, []);

  const setOrder = useCallback((orderedIds) => {
    const current = read();
    const byId = Object.fromEntries(current.map((i) => [i.id, i]));
    const next = orderedIds.map((id) => byId[id]).filter(Boolean);
    write(next);
  }, []);

  const setItemsRaw = useCallback((newItems) => {
    write(newItems);
  }, []);

  return { items, has, add, remove, clear, setOrder, setItems: setItemsRaw };
}
